/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { LOGGER_LOG_TYPE } from 'Config';
import { UserProfile } from 'api/account/models/UserProfile';
import VehiclesService from 'api/vehicles/VehiclesService';
import { VehicleDto } from 'api/vehicles/models/VehicleDto';
import UsersService from 'api/users/UsersService';
import Loading from 'common/services/Loading';
import Logger from 'common/services/Logger';
import { useEffect, useState } from 'react';
import { Breadcrumb, Button, Col, Form, Row } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Reducers } from 'store/types';
import styles from './VehicleScreen.module.scss';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import SearchableSelect from 'common/components/searchableSelect/SearchableSelect';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import DevicesService from 'api/devices/DevicesService';
import DriversService from 'api/drivers/DriversService';

type Props = {
};

const VehicleScreen: React.FC<Props> = () => {
    const { id, type } = useParams<{ id: string, type: string }>();
    const [isDetails, setIsDetails] = useState<boolean>(type === 'details');
    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasVehiclesWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['MY_VEHICLES_WRITE']);
    const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false);
    const [itemToRemove, setItemToRemove] = useState<VehicleDto | null>(null);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [validated, setValidated] = useState(false);
    const [item, setItem] = useState<VehicleDto>();
    const [devices, setDevices] = useState<SelectValueLabel[]>([]);
    const [devicesById, setDevicesById] = useState<Record<string, SelectValueLabel>>({});
    const [drivers, setDrivers] = useState<SelectValueLabel[]>([]);
    const [gpsDeviceConflictMessage, setGpsDeviceConflictMessage] = useState<string | undefined>(undefined);
    const [gpsDeviceInactiveWarning, setGpsDeviceInactiveWarning] = useState<string | undefined>(undefined);
    const [refresh, setRefresh] = useState<number>(1);
    const [formData, setFormData] = useState({
        id: '',
        licensePlate: '',
        designation: '',
        fuelTank: 0,
        companyId: '',
        driverId: '',
        deviceId: '',
        hasIdentificationTag: false,
        rowVersion: ''
    });

    const getData = async () => {
        try {
            let result: any = { id: '', licensePlate: '', companyId: '' };
            Loading.show();
            const allDevices = await DevicesService.getMyDevices(id);
            const byId: Record<string, SelectValueLabel> = {};
            allDevices.forEach(d => { if (d.value) byId[d.value] = d; });
            setDevicesById(byId);

            if (id) {
                result = await VehiclesService.getById(id);
            }

            const visibleDevices = allDevices
                .filter(d => !d.assignedToActive || d.value === result?.deviceId)
                .map(d => ({
                    ...d,
                    label: d.assignedToInactive
                        ? `${d.label} (${t('vehicles.inactive')})`
                        : d.label
                }));
            setDevices(visibleDevices);

            const drivers = await DriversService.getDrivers();
            setDrivers(drivers);

            setItem(result);
            setFormData(result);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get vehicle information', error);
            toast.error(t('messages.error_load_info'));
        } finally {
            Loading.hide();
            setRefresh(refresh + 1);
        }
    };

    useEffect(() => {
        getData().catch(console.error);
    }, [id, type]);

    function handleChange(e: any) {
        const key = e.target.name;
        const value = e.target.value;
        setFormData({ ...formData, [key]: value });

        if (key === 'deviceId') {
            setGpsDeviceConflictMessage(undefined);
            const tv = value ? devicesById[value] : undefined;
            if (tv?.assignedToInactive && value !== item?.deviceId) {
                setGpsDeviceInactiveWarning(t('vehicles.gps_device_in_use_inactive', { name: tv.assignedToVehicleName ?? '' }) ?? undefined);
            } else {
                setGpsDeviceInactiveWarning(undefined);
            }
        }
    }

    const navigateTo = (typeUrl?: string, id?: string) => {
        if (typeUrl) {
            navigate(`/vehicle/${typeUrl}/${id}`);
            setIsDetails(typeUrl === 'details');
        } else {
            navigate('/vehicles');
        }
    }

    const onCancelRemove = () => {
        setItemToRemove(null);
        setShowRemoveModal(false);
    };

    const onRemove = async () => {
        if (itemToRemove === null) {
            toast.error(t('messages.record_delete_error'));
            return;
        }

        try {
            await VehiclesService.remove(itemToRemove);
            onCancelRemove();
            navigateTo();
            toast.success(t('messages.record_delete_success'));
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t delete vehicle', error);
            toast.error(t('messages.record_delete_error'));
        }
    };

    const showRemoveItemDialog = async (item: VehicleDto) => {
        setItemToRemove(item);
        setShowRemoveModal(true);
    }

    const onSubmit = async (event: any) => {
        const form = event.currentTarget;
        event.preventDefault();
        event.stopPropagation();
        setValidated(true);
        if (form.checkValidity() === false) {
            return;
        }
        try {
            Loading.show();
            const model: VehicleDto = {
                id: formData.id,
                fuelTank: formData.fuelTank,
                licensePlate: formData.licensePlate,
                designation: formData.designation,
                deviceId: formData.deviceId,
                driverId: formData.driverId,
                hasIdentificationTag: formData.hasIdentificationTag,
                rowVersion: formData.rowVersion
            };
            if (model && model.id) {
                await VehiclesService.update(model)
                navigateTo('details', id);
            } else if (model) {
                const id = await VehiclesService.create(model)
                navigateTo('details', id);
            }
            Loading.hide();
            toast.success(t('messages.record_save_success'));
        } catch (error: any) {
            if (error?.response?.status === 409) {
                if (error?.response?.data?.code === 'GPS_DEVICE_IN_USE') {
                    const message = error.response.data.message as string;
                    setGpsDeviceConflictMessage(message);
                    toast(message);
                } else {
                    toast(t('messages.vehicle_already_exists'));
                }
            } else {
                Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't update the vehicle with id: ${form.id}`, error);
                toast.error(t('messages.error_load_info'));
            }
            Loading.hide();
        }
    };

    return (
        <ScreenTitle title={t('vehicles.title')}>
            <Breadcrumb>
                <Breadcrumb.Item onClick={() => { navigateTo() }}>
                    {t('vehicles.title')}
                </Breadcrumb.Item>
                <Breadcrumb.Item active>{t(`common.${type}`)}</Breadcrumb.Item>
            </Breadcrumb>

            <div className="bg-white rounded-2xl border shadow-sm p-4" style={{ borderColor: '#e2e8f0' }}>
            <Form onSubmit={onSubmit} noValidate validated={validated} key={refresh}>
                <Row>
                    <Col xs={12}>
                        <Form.Group className="mb-3" controlId="licensePlate">
                            <Form.Label>{t('vehicles.license_plate')}{!isDetails ? '*' : ''}</Form.Label>
                            <Form.Control required name="licensePlate" type="text" defaultValue={item?.licensePlate} readOnly={isDetails} onChange={handleChange} placeholder={t('vehicles.license_plate')} />
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col xs={12}>
                        <Form.Group className="mb-3" controlId="designation">
                            <Form.Label>{t('vehicles.designation')}{!isDetails ? '*' : ''}</Form.Label>
                            <Form.Control required name="designation" type="text" defaultValue={item?.designation} readOnly={isDetails} onChange={handleChange} placeholder={t('vehicles.designation')} />
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col xs={12}>
                        <Form.Group className="mb-3" controlId="fuelTank">
                            <Form.Label>{t('vehicles.fuel_tank')}(l){!isDetails ? '*' : ''}</Form.Label>
                            <Form.Control required name="fuelTank" type="number" defaultValue={item?.fuelTank} readOnly={isDetails} onChange={handleChange} placeholder={t('vehicles.fuel_tank')} />
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col xs={12}>
                        <Form.Group className="mb-3" controlId="driverId">
                            <Form.Label>{t('vehicles.driver')}</Form.Label>
                            <SearchableSelect
                                readOnly={isDetails}
                                name="driverId"
                                value={formData.driverId}
                                options={drivers}
                                placeholder={t('common.select_option') ?? ''}
                                onChange={handleChange}
                            />
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col xs={12}>
                        <Form.Group className="mb-3" controlId="deviceId">
                            <Form.Label>{t('vehicles.device')}</Form.Label>
                            <SearchableSelect
                                readOnly={isDetails}
                                name="deviceId"
                                value={formData.deviceId}
                                options={devices}
                                placeholder={t('common.select_option') ?? ''}
                                onChange={handleChange}
                            />
                            {!!gpsDeviceConflictMessage && <Form.Text className="text-danger">{gpsDeviceConflictMessage}</Form.Text>}
                            {!gpsDeviceConflictMessage && !!gpsDeviceInactiveWarning && <Form.Text className="text-warning">{gpsDeviceInactiveWarning}</Form.Text>}
                        </Form.Group>
                    </Col>
                </Row>
                {devices.find(d => d.value === formData.deviceId)?.supportsIdentificationTag && (
                    <Row>
                        <Col xs={12}>
                            <Form.Group className="mb-3" controlId="hasIdentificationTag">
                                <Form.Check
                                    name="hasIdentificationTag"
                                    type="switch"
                                    label={t('vehicles.has_identification_tag')}
                                    checked={formData.hasIdentificationTag}
                                    disabled={isDetails}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        setFormData({ ...formData, hasIdentificationTag: e.target.checked });
                                    }}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                )}
                <div className={styles.buttonsContainer}>
                    <Button variant="secondary" type="button" className={styles.button} onClick={() => navigateTo()}>
                        {t('common.cancel')}
                    </Button>
                    {isDetails && hasVehiclesWritePolicy &&
                        <Button variant="danger" type="button" className={styles.button} onClick={() => showRemoveItemDialog(item as VehicleDto)} >
                            {t('common.delete')}
                        </Button>
                    }
                    {isDetails && hasVehiclesWritePolicy &&
                        <Button variant="warning" type="button" className={styles.button} onClick={() => navigateTo('edit', item?.id) }>
                            {t('common.edit')}
                        </Button>
                    }
                    {!isDetails && hasVehiclesWritePolicy &&
                        <Button variant="primary" type="submit" className={styles.button}>
                            {t('common.save')}
                        </Button>
                    }
                </div>
            </Form>
            </div>
            <QuestionYesNo onNo={onCancelRemove} onYes={onRemove} isVisible={showRemoveModal} message={t('messages.remove_record_with_ident', { name: item?.licensePlate ?? '' })} />
        </ScreenTitle>
    );
};

export default VehicleScreen;
